import React, { useState, useEffect } from 'react';
import { Website, BillingPeriodicity, PaymentStatus } from '../types';
import { 
  CalendarIcon, 
  CurrencyEuroIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

interface BillingStats {
  totalRevenue: number;
  pendingInvoices: number;
  overdueInvoices: number;
  upcomingInvoices: number;
  revenueByPeriod: {
    monthly: number;
    quarterly: number;
    yearly: number;
  };
}

interface BillingFilter {
  period: 'all' | 'this_month' | 'next_month' | 'overdue';
  amount: 'all' | 'low' | 'medium' | 'high';
  status: 'all' | PaymentStatus;
  contractType: 'all' | string;
}

const BillingPage: React.FC = () => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [filteredWebsites, setFilteredWebsites] = useState<Website[]>([]);
  const [stats, setStats] = useState<BillingStats>({
    totalRevenue: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    upcomingInvoices: 0,
    revenueByPeriod: { monthly: 0, quarterly: 0, yearly: 0 }
  });
  const [filters, setFilters] = useState<BillingFilter>({
    period: 'all',
    amount: 'all',
    status: 'all',
    contractType: 'all'
  });
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<keyof Website>('nextBillingDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Charger les sites web
  useEffect(() => {
    loadWebsites();
  }, []);

  // Filtrer et trier les sites
  useEffect(() => {
    filterAndSortWebsites();
  }, [websites, filters, sortField, sortDirection]);

  // Calculer les statistiques
  useEffect(() => {
    calculateStats();
  }, [filteredWebsites]);

  const loadWebsites = async () => {
    try {
      setLoading(true);
      const response = await api.get('/websites');
      setWebsites(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortWebsites = () => {
    let filtered = websites.filter(website => {
      // Exclure les projets internes de la facturation
      if (website.contractType === 'Projet Interne' || website.status === 'internal') {
        return false;
      }
      
      // Filtre par période
      if (filters.period !== 'all') {
        const today = new Date();
        const nextBilling = website.nextBillingDate ? new Date(website.nextBillingDate) : null;
        
        if (!nextBilling) return false;
        
        const diffDays = Math.ceil((nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        switch (filters.period) {
          case 'this_month':
            return diffDays >= 0 && diffDays <= 30;
          case 'next_month':
            return diffDays > 30 && diffDays <= 60;
          case 'overdue':
            return diffDays < 0;
          default:
            return true;
        }
      }

      // Filtre par montant
      if (filters.amount !== 'all') {
        switch (filters.amount) {
          case 'low':
            return website.amount < 100;
          case 'medium':
            return website.amount >= 100 && website.amount <= 500;
          case 'high':
            return website.amount > 500;
          default:
            return true;
        }
      }

      // Filtre par statut
      if (filters.status !== 'all') {
        return website.paymentStatus === filters.status;
      }

      // Filtre par type de contrat
      if (filters.contractType !== 'all') {
        return website.contractType === filters.contractType;
      }

      return true;
    });

    // Tri
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortField === 'nextBillingDate' || sortField === 'lastBillingDate') {
        const aDate = aValue ? new Date(aValue as string).getTime() : 0;
        const bDate = bValue ? new Date(bValue as string).getTime() : 0;
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      
      if (sortField === 'amount') {
        return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
      }
      
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    setFilteredWebsites(filtered);
  };

  const calculateStats = () => {
    const today = new Date();
    let totalRevenue = 0;
    let pendingInvoices = 0;
    let overdueInvoices = 0;
    let upcomingInvoices = 0;
    const revenueByPeriod = { monthly: 0, quarterly: 0, yearly: 0 };

    // Filtrer les sites pour exclure les projets internes
    const billingWebsites = websites.filter(website => website.contractType !== 'Projet Interne' && website.status !== 'internal');
    
    billingWebsites.forEach(website => {
      totalRevenue += website.amount || 0;
      
      if (website.nextBillingDate) {
        const nextBilling = new Date(website.nextBillingDate);
        const diffDays = Math.ceil((nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
          overdueInvoices++;
        } else if (diffDays <= 15) {
          upcomingInvoices++;
        }
      }
      
      if (website.paymentStatus === 'en_attente' || website.paymentStatus === 'en_retard') {
        pendingInvoices++;
      }

      // Calculer le CA par période
      if (website.billingPeriodicity === 'mensuel') {
        revenueByPeriod.monthly += website.amount || 0;
      } else if (website.billingPeriodicity === 'trimestriel') {
        revenueByPeriod.quarterly += website.amount || 0;
      } else if (website.billingPeriodicity === 'annuel') {
        revenueByPeriod.yearly += website.amount || 0;
      }
    });

    setStats({
      totalRevenue,
      pendingInvoices,
      overdueInvoices,
      upcomingInvoices,
      revenueByPeriod
    });
  };

  const getStatusIcon = (website: Website) => {
    if (!website.nextBillingDate) return null;
    
    const today = new Date();
    const nextBilling = new Date(website.nextBillingDate);
    const diffDays = Math.ceil((nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" title="En retard" />;
    } else if (diffDays <= 15) {
      return <ClockIcon className="h-5 w-5 text-yellow-500" title="À envoyer bientôt" />;
    } else {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" title="Dans les délais" />;
    }
  };

  const getStatusColor = (website: Website) => {
    if (!website.nextBillingDate) return 'text-gray-500';
    
    const today = new Date();
    const nextBilling = new Date(website.nextBillingDate);
    const diffDays = Math.ceil((nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'text-red-600';
    if (diffDays <= 15) return 'text-yellow-600';
    return 'text-green-600';
  };

  const exportToCSV = () => {
    const headers = ['Client', 'URL', 'Type contrat', 'Montant', 'Prochaine facturation', 'Statut'];
    // Exclure les projets internes de l'export aussi
    const exportWebsites = filteredWebsites.filter(website => website.contractType !== 'Projet Interne' && website.status !== 'internal');
    const csvData = exportWebsites.map(website => [
      website.clientName,
      website.url,
      website.contractType,
      `${website.amount} €`,
      website.nextBillingDate,
      website.paymentStatus
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `factures_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSort = (field: keyof Website) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Facturation</h1>
          <p className="text-gray-600 dark:text-gray-400">Gestion des factures et suivi des paiements</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
          Exporter CSV
        </button>
      </div>

      {/* Tableaux de bord */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <CurrencyEuroIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">CA Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalRevenue.toLocaleString()} €</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">À envoyer (15j)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.upcomingInvoices}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En retard</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.overdueInvoices}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En attente</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pendingInvoices}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filtres</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Période
            </label>
            <select
              value={filters.period}
              onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value as any }))}
              className="input-field"
            >
              <option value="all">Toutes les périodes</option>
              <option value="this_month">Ce mois (≤ 30j)</option>
              <option value="next_month">Prochain mois (31-60j)</option>
              <option value="overdue">En retard</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Montant
            </label>
            <select
              value={filters.amount}
              onChange={(e) => setFilters(prev => ({ ...prev, amount: e.target.value as any }))}
              className="input-field"
            >
              <option value="all">Tous les montants</option>
              <option value="low">&lt; 100€</option>
              <option value="medium">100-500€</option>
              <option value="high">&gt; 500€</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Statut
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
              className="input-field"
            >
              <option value="all">Tous les statuts</option>
              <option value="payé">Payé</option>
              <option value="en_attente">En attente</option>
              <option value="en_retard">En retard</option>
              <option value="annulé">Annulé</option>
              <option value="remboursé">Remboursé</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type de contrat
            </label>
            <select
              value={filters.contractType}
              onChange={(e) => setFilters(prev => ({ ...prev, contractType: e.target.value }))}
              className="input-field"
            >
              <option value="all">Tous les types</option>
              {Array.from(new Set(websites.map(w => w.contractType).filter(Boolean))).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Liste des factures */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Factures ({filteredWebsites.length})
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              ⚠️ Les projets internes sont exclus de la facturation
            </p>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Statut
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('clientName')}
                >
                  Client
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('contractType')}
                >
                  Type contrat
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('amount')}
                >
                  Montant
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('nextBillingDate')}
                >
                  Prochaine facturation
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('paymentStatus')}
                >
                  Statut paiement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredWebsites.map((website) => (
                <tr key={website.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusIcon(website)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {website.clientName}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {website.url}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {website.contractType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {website.amount ? `${website.amount} €` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getStatusColor(website)}`}>
                      {website.nextBillingDate ? new Date(website.nextBillingDate).toLocaleDateString() : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      website.paymentStatus === 'payé' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                      website.paymentStatus === 'en_attente' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                      website.paymentStatus === 'en_retard' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                    }`}>
                      {website.paymentStatus || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => window.open(`/sites/${website.id}`, '_blank')}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BillingPage; 